import type { ComputedRef, Ref } from 'vue';
import { formatMoney } from '@/domain/selection';
import type {
  EntrScreen,
  EntrSession,
  PaymentFailure,
  PaymentQuote,
  SelectionItem,
} from '@/domain/types';
import {
  PAYMENT_TIMEOUT_CODE,
  SESSION_UNAVAILABLE_FAILURE,
} from '@/kiosk-core/payment/paymentStatus';
import { CARD_WINDOW_MS } from '@/kiosk-core/payment/terminalPayment';
import { paidCopyAmounts } from '@/kiosk-core/printing/printingRuntime';
import { createAbortError, delay } from '@/services/fetchDriver';
import type {
  EntrPrintedPhotoType,
  EntrRuntime,
  PrintCopiesSummary,
} from '@/transport/types';
import type {
  AttractResetReason,
  CheckoutContext,
  ControllerSheet,
} from './controllerTypes';
import {
  createCheckoutKey,
  createEpisodeNonce,
  createIdempotencyKey,
  createPaymentIdempotencyKey,
  createPrintedCoverageKey,
  createShowError,
} from './controllerUtils';
import type { PrintedCoverageWritebackQueue } from './printedCoverageWritebackQueue';

export interface CheckoutFlowObserver {
  addBreadcrumb(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void;
  captureException(error: unknown, tag: string): void;
  captureMessage(
    message: string,
    level: 'error' | 'warning',
    extra?: Record<string, unknown>
  ): void;
}

export interface UseCheckoutFlowOptions {
  runtime: EntrRuntime;
  session: Ref<EntrSession | null>;
  quote: ComputedRef<PaymentQuote>;
  selectionItems: ComputedRef<SelectionItem[]>;
  paymentEnabled: ComputedRef<boolean>;
  paymentState: Ref<'idle' | 'waiting' | 'failed'>;
  printState: Ref<'idle' | 'printing' | 'failed'>;
  staffPaymentBypassEnabled: boolean;
  // Support code shown to visitors on the exhausted-retry error sheet — NOT
  // the staff override (that is voucherCode, scanned as a QR).
  helpCode: string;
  // Opaque token a staff voucher QR must equal to override payment. Empty
  // string disables the override (no code configured).
  voucherCode: string;
  getScreen: () => EntrScreen;
  setScreen: (screen: EntrScreen) => void;
  setSheet: (sheet: ControllerSheet | null) => void;
  resetToAttract: (reason: AttractResetReason) => Promise<void>;
  scheduleResetToAttract: (
    reason: AttractResetReason,
    delayMs: number,
    shouldReset?: () => boolean
  ) => () => void;
  printedCoverageWritebacks: PrintedCoverageWritebackQueue;
  observer: CheckoutFlowObserver;
}

const FAREWELL_DELAY_MS = 5000;
const PRINT_SCREEN_MIN_MS = 5000;
const MAX_PAYMENT_ATTEMPTS = 3;
// Stripe refuses charges under ~€0.50 and the backend mirrors that with a hard
// 50-cent floor on payment-intent create (422). A quote in (0, 50) can only
// come from a misconfigured kiosk price — attempting it dead-ends on a 422
// every retry, so fail fast to the staff sheet instead of hammering it.
const STRIPE_MIN_CHARGE_CENTS = 50;
const MAX_PRINT_ATTEMPTS = 3;
// One watchdog for each copy, set again when the batch starts the next copy.
// A copy before its first send takes up to its 600 s pre-send poll window
// after its submit (whose retries can take up to 45 s). A late first send
// gives the copy a final_by after that window, and the poll then moves the
// watchdog to the end of its own poll. Thus the watchdog fires only when the
// print runtime itself does not answer. Its retry sends the same print key,
// thus it cannot print a copy two times.
const PRINT_HANG_TIMEOUT_MS = 660_000;
const PRINT_HANG_FAILURE_RESET_MS = 5000;
// When nobody touches a failed print sheet or a final failure sheet, the kiosk
// goes to the start screen after this time.
const FAILURE_SHEET_RESET_MS = 30_000;
const PHOTOS_NOT_READY_DISMISS_MS = 7000;
// The card window starts at the answer of the send. The create before it can
// take approximately 60 s, with a timeout and one retry, and the send 30 s,
// with no retry. This limit plus the idle timeout is longer than the two
// requests and the window together.
const CARD_WAIT_LIMIT_MS = CARD_WINDOW_MS + 60_000;

interface SheetCopy {
  titleKey: string;
  bodyKey: string;
}

const INSUFFICIENT_FUNDS_CODES: ReadonlySet<string> = new Set([
  'insufficient_funds',
  'card_velocity_exceeded',
]);
const EXPIRED_CARD_CODE = 'expired_card';
// The card can pay when the visitor inserts it and enters the PIN. A tap
// cannot give the PIN.
const PIN_REQUIRED_CODES: ReadonlySet<string> = new Set([
  'online_or_offline_pin_required',
  'offline_pin_required',
]);

function errorCopy(name: string): SheetCopy {
  return { titleKey: `errors.${name}Title`, bodyKey: `errors.${name}Body` };
}

// Only a card failure tells the visitor to use a different card. The
// generic text says "try again", because the cause is not known.
function paymentFailureCopy(failure: PaymentFailure | undefined): SheetCopy {
  if (!failure) return errorCopy('paymentFailed');
  if (failure.code === PAYMENT_TIMEOUT_CODE) return errorCopy('paymentTimeout');
  switch (failure.category) {
    case 'reader_error':
      return errorCopy('paymentReaderUnavailable');
    case 'processing_error':
      return errorCopy('paymentNotCompleted');
    case 'card_declined':
    case 'card_error':
      return cardFailureCopy(failure);
    default:
      return errorCopy('paymentFailed');
  }
}

// Stripe also gives decline reasons such as a lost, stolen or fraud card. The
// visitor must never see these reasons, thus each of them shows the same
// "card declined" text.
function cardFailureCopy(failure: PaymentFailure): SheetCopy {
  const codes = [failure.decline_code, failure.stripe_code, failure.code];
  if (codes.some((code) => code && PIN_REQUIRED_CODES.has(code))) {
    return errorCopy('paymentPinRequired');
  }
  if (codes.includes(EXPIRED_CARD_CODE)) return errorCopy('paymentCardExpired');
  if (codes.some((code) => code && INSUFFICIENT_FUNDS_CODES.has(code))) {
    return errorCopy('paymentInsufficientFunds');
  }
  return failure.category === 'card_declined'
    ? errorCopy('paymentDeclined')
    : errorCopy('paymentCardNotAccepted');
}

// A print writeback can name its prepaid bucket only when the whole checkout
// is unambiguously one photo type; a mixed group+portrait print stays
// type-less so Fever decrements the aggregate instead of the wrong pool.
function singlePrintedPhotoType(
  context: CheckoutContext
): EntrPrintedPhotoType | undefined {
  const types = new Set(context.items.map((item) => item.photo.type));
  if (types.size !== 1) return undefined;
  return types.has('group') ? 'group_photo' : 'portrait_photo';
}

// The wedge scanner may deliver a code with surrounding whitespace or mixed
// case; fold both sides the same way so a scanned voucher matches the
// configured token regardless of how the QR was encoded.
function normalizeVoucherCode(value: string): string {
  return value.trim().toUpperCase();
}

type SettledPrintBody = Pick<
  NonNullable<ControllerSheet>,
  'bodyKey' | 'bodyParams' | 'helpCode'
>;

function settledPrintBody(
  copies: PrintCopiesSummary,
  currency: string,
  helpCode: string
): SettledPrintBody {
  if (copies.refundFailed > 0) {
    return { bodyKey: 'errors.printFailedPaidBody', helpCode };
  }
  const money = (cents: number) => formatMoney(cents, currency);
  if (copies.refunded > 0 && copies.notCharged > 0) {
    return {
      bodyKey: 'errors.printRefundedNotChargedBody',
      bodyParams: {
        refunded: money(copies.refundedCents),
        notCharged: money(copies.notChargedCents),
      },
    };
  }
  if (copies.refunded > 0) {
    return {
      bodyKey: 'errors.printRefundedBody',
      bodyParams: { amount: money(copies.refundedCents) },
    };
  }
  if (copies.notCharged > 0) {
    return {
      bodyKey: 'errors.printNotChargedBody',
      bodyParams: { amount: money(copies.notChargedCents) },
    };
  }
  // Only copies without a price did not print: the visitor paid nothing
  // for them.
  return { bodyKey: 'errors.printFailedBody' };
}

export function useCheckoutFlow(options: UseCheckoutFlowOptions) {
  const showError = createShowError(options.setSheet);
  let checkoutSequence = 0;
  // Rotated in resetForAttract (fires on reset-to-attract AND on every new
  // session) and held across BACK + retry, so deterministic payment identities
  // are scoped to one visit — see createEpisodeNonce in controllerUtils.
  let episodeNonce = createEpisodeNonce();
  let activeCheckout: CheckoutContext | null = null;
  let processingContinue = false;
  let paymentAttempts = 0;
  let printAttempts = 0;
  let paymentIdempotencyKey: string | null = null;
  let printIdempotencyKey: string | null = null;
  let isPaymentSettled = false;
  // A voucher scan also settles the payment, but only a card charge takes
  // money from the visitor.
  let isCardCharged = false;
  // The Stripe payment intent of the card charge. Each paid copy carries it,
  // thus the backend does not charge (or refunds) a copy that did not print.
  let cardPaymentId: string | null = null;
  let printHangTimer: number | null = null;
  let printHangStartedAtMs = 0;
  let printHangEndsAtMs = 0;
  let cancelFailureReset: (() => void) | null = null;
  let activePrintAbortController: AbortController | null = null;
  let cardWaitStartedAt = 0;

  async function continueFromSelection(): Promise<void> {
    if (processingContinue) return;
    if (options.quote.value.totalSelected === 0 || !options.session.value) {
      return;
    }

    processingContinue = true;
    try {
      const refreshed = await refreshSessionForCheckout();
      if (refreshed === 'photos-not-ready') return;

      const checkout = beginCheckout();
      if (!checkout) return;

      if (options.paymentEnabled.value && checkout.quote.amountDueCents > 0) {
        paymentAttempts = 0;
        paymentIdempotencyKey = createPaymentIdempotencyKey(checkout);
        isPaymentSettled = false;
        options.setScreen('card-payment');
        await payByCard(checkout);
        return;
      }

      await startPrinting(checkout);
    } finally {
      processingContinue = false;
    }
  }

  async function refreshSessionForCheckout(): Promise<
    'ok' | 'photos-not-ready' | 'snapshot-fallback'
  > {
    const currentSession = options.session.value;
    if (!currentSession?.ticketCode) return 'ok';

    try {
      const result = await options.runtime.data.getSessionByTicketCode(
        currentSession.ticketCode
      );
      if (!result.ok) {
        if (result.reason === 'photos-not-ready') {
          showError('errors.photosNotReadyTitle', 'errors.photosNotReadyBody', {
            autoDismissMs: PHOTOS_NOT_READY_DISMISS_MS,
          });
          return 'photos-not-ready';
        }
        options.observer.addBreadcrumb(
          'entr.checkout',
          'Refresh returned not-found, using snapshot'
        );
        return 'snapshot-fallback';
      }
      options.session.value = { ...result.value, id: currentSession.id };
      return 'ok';
    } catch (error) {
      options.observer.captureException(error, 'refresh_session_for_checkout');
      return 'snapshot-fallback';
    }
  }

  async function payByCard(context = activeCheckout): Promise<void> {
    if (!context || !isActiveCheckout(context)) return;
    if (isPaymentSettled) {
      await startPrinting(context);
      return;
    }

    if (
      context.quote.amountDueCents > 0 &&
      context.quote.amountDueCents < STRIPE_MIN_CHARGE_CENTS
    ) {
      options.paymentState.value = 'failed';
      options.observer.captureMessage(
        'entr payment blocked: quote below Stripe minimum charge',
        'error',
        {
          amount_due_cents: context.quote.amountDueCents,
          currency: context.quote.currency,
          session_id: context.sessionId,
        }
      );
      showFinalFailure('payment');
      return;
    }

    paymentIdempotencyKey ??= createPaymentIdempotencyKey(context);
    paymentAttempts += 1;

    options.paymentState.value = 'waiting';
    cardWaitStartedAt = Date.now();
    options.observer.addBreadcrumb('entr.payment', 'Payment attempt', {
      attempt: paymentAttempts,
    });

    let result: Awaited<ReturnType<typeof options.runtime.payment.payCard>>;
    try {
      result = await options.runtime.payment.payCard({
        sessionId: context.sessionId,
        quote: context.quote,
        idempotencyKey: paymentIdempotencyKey,
        checkoutKey: createCheckoutKey(context),
        // The same quote as the payment of startPrinting, thus the declared
        // copies are the copies that carry the payment intent.
        copyAmountsCents: paidCopyAmounts(context.quote.copyAmountsCents),
      });
    } catch (error) {
      options.observer.captureException(error, 'pay_card');
      result = { ok: false, reason: 'network-error' };
    }

    if (!isActiveCheckout(context) || options.getScreen() !== 'card-payment') {
      return;
    }

    if (!result.ok) {
      options.paymentState.value = 'failed';
      if (result.failure?.code === SESSION_UNAVAILABLE_FAILURE.code) {
        showSessionUnavailable();
        return;
      }
      if (paymentAttempts >= MAX_PAYMENT_ATTEMPTS) {
        showFinalFailure('payment');
        return;
      }
      const copy = paymentFailureCopy(result.failure);
      showError(copy.titleKey, copy.bodyKey, {
        retry: () => void payByCard(context),
      });
      return;
    }

    isPaymentSettled = true;
    isCardCharged = true;
    cardPaymentId = result.value.paymentId;
    options.paymentState.value = 'idle';
    options.observer.addBreadcrumb('entr.payment', 'Payment settled', {
      attempt: paymentAttempts,
    });
    await startPrinting(context);
  }

  async function startPrinting(context = activeCheckout): Promise<void> {
    if (!context || !isActiveCheckout(context)) return;

    clearFailureReset();
    printIdempotencyKey ??= createIdempotencyKey(
      'print',
      context.externalEntrId
    );
    printAttempts += 1;
    const printAbortController = new AbortController();
    activePrintAbortController = printAbortController;

    options.setScreen('printing');
    schedulePrintHangTimer();
    options.printState.value = 'printing';
    options.observer.addBreadcrumb('entr.print', 'Print attempt', {
      attempt: printAttempts,
    });
    const startedAt = Date.now();
    // groupId (context.groupId) is the writeback identity — OUR ledger key, set
    // for both browse and ticket sessions. ticketCode is now optional and only
    // rides along for the best-effort Fever forward on ticket sessions.
    const ticketCode = options.session.value?.ticketCode;

    let result: Awaited<
      ReturnType<typeof options.runtime.print.printSelection>
    >;
    try {
      result = await options.runtime.print.printSelection({
        sessionId: context.sessionId,
        // Option 2: the synthetic id rides the wire (submit body) while
        // sessionId stays the real UUID that keys batch/idempotency identity.
        wireSessionId: context.externalEntrId ?? `entr-api-${context.groupId}`,
        items: context.items,
        idempotencyKey: printIdempotencyKey,
        payment:
          isCardCharged && cardPaymentId
            ? {
                paymentIntentId: cardPaymentId,
                paymentSessionId: context.sessionId,
                copyAmountsCents: context.quote.copyAmountsCents,
              }
            : null,
        signal: printAbortController.signal,
        onConfirmedCoverage: (printedCount) =>
          recordPrintedCoverageDelta(context, printedCount, ticketCode),
        onCopyStarted: () => {
          if (activePrintAbortController === printAbortController) {
            schedulePrintHangTimer();
          }
        },
        onCopyPollEnd: (pollEndsAtMs) => {
          if (activePrintAbortController === printAbortController) {
            extendPrintHangTimer(pollEndsAtMs);
          }
        },
      });
    } catch (error) {
      options.observer.captureException(error, 'print_selection');
      result = { ok: false, reason: 'network-error' };
    }
    // An attempt that the hang timeout stopped can return after the visitor
    // started a retry. That late return must not clear the timer of the retry.
    if (activePrintAbortController === printAbortController) {
      activePrintAbortController = null;
      clearPrintHangTimer();
    }

    await recordPrintedCoverageDelta(
      context,
      result.ok ? result.value.printedCount : (result.printedCount ?? 0),
      ticketCode
    );

    const isStale = (): boolean =>
      printAbortController.signal.aborted ||
      !isActiveCheckout(context) ||
      options.getScreen() !== 'printing';
    if (isStale()) return;

    const elapsed = Date.now() - startedAt;
    if (elapsed < PRINT_SCREEN_MIN_MS) {
      try {
        await delay(
          PRINT_SCREEN_MIN_MS - elapsed,
          printAbortController.signal,
          'Operation aborted'
        );
      } catch {
        return;
      }
    }

    if (isStale()) return;

    if (!result.ok) {
      options.printState.value = 'failed';
      if (result.copies && result.copies.unsettled === 0) {
        // Each copy printed or got its final answer: a retry has nothing to
        // do, thus the sheet tells the result and has no retry.
        showSettledPrintResult(result.copies, context);
        schedulePrintFailureReset();
        return;
      }
      if (printAttempts >= MAX_PRINT_ATTEMPTS) {
        showFinalFailure('print');
        return;
      }
      showError(
        'errors.printFailedTitle',
        printFailureBodyKey('errors.printFailedBody'),
        {
          retry: () => void startPrinting(context),
          ...(isCardCharged ? { helpCode: options.helpCode } : {}),
        }
      );
      schedulePrintFailureReset();
      return;
    }

    options.printState.value = 'idle';
    options.observer.addBreadcrumb('entr.print', 'Print settled', {
      printedCount: result.value.printedCount,
      jobId: result.value.jobId,
    });
    activeCheckout = null;
    options.setScreen('farewell');
    options.scheduleResetToAttract('farewell', FAREWELL_DELAY_MS);
  }

  async function recordPrintedCoverageDelta(
    context: CheckoutContext,
    printedCount: number,
    ticketCode = options.session.value?.ticketCode
  ): Promise<void> {
    const confirmedCount = Math.max(0, Math.floor(printedCount));
    // OUR ledger keys on the group, so a browse session (no ticketCode) now
    // records too — we are the source of truth, not Fever. ticketCode still
    // rides along for the best-effort Fever forward when present.
    const groupId = context.groupId;
    if (!groupId) {
      // No group identity at all — rare and permissive: advance the local
      // marker so we don't loop, and signal once.
      if (confirmedCount > context.printedCoverageRecorded) {
        options.observer.captureMessage(
          'ENTR photo_printed skipped: no group identity',
          'warning',
          {
            session_id: context.sessionId,
            external_entr_id: context.externalEntrId ?? null,
            printed_count: confirmedCount,
          }
        );
        context.printedCoverageRecorded = confirmedCount;
      }
      return;
    }
    const coverageKey = createPrintedCoverageKey(context);
    const photoType = singlePrintedPhotoType(context);
    context.printedCoverageRecorded =
      await options.printedCoverageWritebacks.recordDelta({
        coverageKey,
        groupId,
        ...(ticketCode ? { ticketCode } : {}),
        confirmedCount,
        recordedCount: context.printedCoverageRecorded,
        ...(photoType ? { photoType } : {}),
      });
  }

  async function cancelActivePaymentForReset(): Promise<void> {
    if (
      options.getScreen() === 'card-payment' &&
      activeCheckout &&
      !isPaymentSettled
    ) {
      try {
        await options.runtime.payment.cancelActivePayment();
      } catch (error) {
        options.observer.captureException(error, 'cancel_payment_on_reset');
      }
    }
  }

  function resetForAttract(): void {
    abortActivePrint();
    activeCheckout = null;
    clearPrintHangTimer();
    clearFailureReset();
    options.paymentState.value = 'idle';
    options.printState.value = 'idle';
    paymentAttempts = 0;
    printAttempts = 0;
    paymentIdempotencyKey = null;
    printIdempotencyKey = null;
    isPaymentSettled = false;
    isCardCharged = false;
    cardPaymentId = null;
    // New visit, new payment-identity scope: without this rotation a re-scan
    // of the same group + the same selection reproduces the previous visit's
    // Stripe idempotency key and replays its settled PaymentIntent — one paid
    // print becomes unlimited free reprints.
    episodeNonce = createEpisodeNonce();
  }

  function goBackFromPayment(): boolean {
    if (options.getScreen() !== 'card-payment') return false;
    clearFailureReset();
    activeCheckout = null;
    options.paymentState.value = 'idle';
    paymentIdempotencyKey = null;
    isPaymentSettled = false;
    void options.runtime.payment.cancelActivePayment();
    options.setScreen('select-photos');
    return true;
  }

  // Staff voucher override, triggered by scanning a voucher QR on the payment
  // screen (the reader has no keyboard, so the old typed code was unusable).
  // 'accepted' → payment skipped, printing started; 'rejected' → wrong/absent
  // code while on the payment screen (caller shows an invalid toast); 'ignored'
  // → not a voucher context, so the scan should fall through to ticket lookup.
  function tryVoucherScan(
    scannedCode: string
  ): 'accepted' | 'rejected' | 'ignored' {
    if (!options.staffPaymentBypassEnabled) return 'ignored';
    if (options.getScreen() !== 'card-payment') return 'ignored';
    const context = activeCheckout;
    if (!context || !isActiveCheckout(context)) return 'ignored';

    // On the payment screen every scan is a voucher attempt (a ticket can't be
    // scanned mid-payment), so a non-match is consumed as 'rejected' rather
    // than leaking into a ticket lookup.
    const expected = normalizeVoucherCode(options.voucherCode);
    if (!expected || normalizeVoucherCode(scannedCode) !== expected) {
      options.observer.addBreadcrumb('entr.payment', 'Voucher scan rejected');
      return 'rejected';
    }

    options.setSheet(null);
    options.paymentState.value = 'idle';
    isPaymentSettled = true;
    options.observer.addBreadcrumb('entr.payment', 'Voucher scan accepted', {
      amount_cents: context.quote.amountDueCents,
    });
    void options.runtime.payment.cancelActivePayment();
    void startPrinting(context);
    return 'accepted';
  }

  // While the reader waits for a card, the idle reset must not cancel it. A
  // payment that stays in 'waiting' after the limit has a fault, and the idle
  // reset then ends it.
  function isWaitingForCard(): boolean {
    return (
      options.paymentState.value === 'waiting' &&
      Date.now() - cardWaitStartedAt < CARD_WAIT_LIMIT_MS
    );
  }

  function onLeavingPrinting(): void {
    clearPrintHangTimer();
    clearFailureReset();
  }

  function stop(): void {
    clearPrintHangTimer();
    clearFailureReset();
    abortActivePrint();
  }

  function beginCheckout(): CheckoutContext | null {
    const currentSession = options.session.value;
    if (!currentSession) return null;

    checkoutSequence += 1;
    isCardCharged = false;
    cardPaymentId = null;
    activeCheckout = {
      id: checkoutSequence,
      sessionId: currentSession.id,
      episodeNonce,
      externalEntrId: currentSession.externalEntrId,
      groupId: currentSession.group.id,
      quote: options.quote.value,
      items: options.selectionItems.value,
      printedCoverageRecorded: 0,
    };
    return activeCheckout;
  }

  function isActiveCheckout(context: CheckoutContext): boolean {
    return (
      activeCheckout?.id === context.id &&
      activeCheckout.sessionId === context.sessionId &&
      options.session.value?.id === context.sessionId
    );
  }

  // A deleted session has no photos to print, thus the sheet has no retry.
  // The close of the sheet takes the visitor back to the start screen.
  function showSessionUnavailable(): void {
    options.observer.addBreadcrumb(
      'entr.payment',
      'Payment refused: session unavailable'
    );
    options.setSheet({
      kind: 'error',
      titleKey: 'errors.paymentSessionUnavailableTitle',
      bodyKey: 'errors.paymentSessionUnavailableBody',
      helpCode: options.helpCode,
    });
  }

  // After the third failure, the sheet has no retry. A new payment or a new
  // print is possible only in a new visit.
  function showFinalFailure(scope: 'payment' | 'print'): void {
    const isPayment = scope === 'payment';
    const reason: AttractResetReason = isPayment
      ? 'payment-failure-final'
      : 'print-failure-final';

    options.observer.addBreadcrumb('entr.help', 'Final failure sheet shown', {
      scope,
    });
    options.setSheet({
      kind: 'error',
      titleKey: isPayment
        ? 'errors.paymentRetryExhaustedTitle'
        : 'errors.printRetryExhaustedTitle',
      bodyKey: isPayment
        ? 'errors.paymentRetryExhaustedBody'
        : printFailureBodyKey('errors.printRetryExhaustedBody'),
      helpCode: options.helpCode,
      exit: () => void options.resetToAttract(reason),
    });
    scheduleFailureReset(
      reason,
      isPayment ? isPaymentFailureShown : isPrintFailureShown
    );
  }

  function schedulePrintHangTimer(): void {
    printHangStartedAtMs = Date.now();
    setPrintHangTimer(printHangStartedAtMs + PRINT_HANG_TIMEOUT_MS);
  }

  function setPrintHangTimer(endsAtMs: number): void {
    clearPrintHangTimer();
    printHangEndsAtMs = endsAtMs;
    printHangTimer = window.setTimeout(() => {
      printHangTimer = null;
      onPrintHangTimeout();
    }, endsAtMs - Date.now());
  }

  // Only a later end moves the watchdog: a copy without final_by keeps the
  // 660 s of its start.
  function extendPrintHangTimer(pollEndsAtMs: number): void {
    if (printHangTimer === null || pollEndsAtMs <= printHangEndsAtMs) return;
    setPrintHangTimer(pollEndsAtMs);
  }

  function clearPrintHangTimer(): void {
    if (printHangTimer !== null) {
      window.clearTimeout(printHangTimer);
      printHangTimer = null;
    }
  }

  function schedulePrintFailureReset(): void {
    scheduleFailureReset('print-failure', isPrintFailureShown);
  }

  function scheduleFailureReset(
    reason: AttractResetReason,
    shouldReset: () => boolean
  ): void {
    clearFailureReset();
    cancelFailureReset = options.scheduleResetToAttract(
      reason,
      FAILURE_SHEET_RESET_MS,
      shouldReset
    );
  }

  function clearFailureReset(): void {
    cancelFailureReset?.();
    cancelFailureReset = null;
  }

  function isPrintFailureShown(): boolean {
    return (
      options.getScreen() === 'printing' &&
      options.printState.value === 'failed'
    );
  }

  // A staff voucher scan on the final payment sheet starts the print. The
  // payment state then changes, and the reset does not occur.
  function isPaymentFailureShown(): boolean {
    return (
      options.getScreen() === 'card-payment' &&
      options.paymentState.value === 'failed'
    );
  }

  function abortActivePrint(): void {
    const controller = activePrintAbortController;
    activePrintAbortController = null;
    if (controller && !controller.signal.aborted) {
      controller.abort(createAbortError('Operation aborted'));
    }
  }

  // A copy without a final answer can still print, thus a visitor who paid by
  // card gets a text that sends the visitor to staff and does not tell what
  // the visitor pays.
  function printFailureBodyKey(unpaidBodyKey: string): string {
    return isCardCharged ? 'errors.printFailedPaidBody' : unpaidBodyKey;
  }

  // The backend does not charge a paid copy that did not print (a held
  // payment), or refunds it (a payment captured earlier). The sheet tells
  // what printed and what the visitor does not pay. The staff text with the
  // help code stays for a paid copy whose refund did not succeed.
  function showSettledPrintResult(
    copies: PrintCopiesSummary,
    context: CheckoutContext
  ): void {
    options.observer.addBreadcrumb(
      'entr.print',
      'Print settled with copies that did not print',
      {
        total: copies.total,
        printed: copies.printed,
        not_charged: copies.notCharged,
        not_charged_cents: copies.notChargedCents,
        refunded: copies.refunded,
        refunded_cents: copies.refundedCents,
        refund_failed: copies.refundFailed,
        failed: copies.failed,
      }
    );
    const title =
      copies.printed > 0
        ? {
            titleKey: 'errors.printPartialTitle',
            titleParams: { printed: copies.printed, total: copies.total },
          }
        : { titleKey: 'errors.printFailedTitle' };
    options.setSheet({
      kind: 'error',
      ...title,
      ...settledPrintBody(copies, context.quote.currency, options.helpCode),
    });
  }

  function onPrintHangTimeout(): void {
    if (options.getScreen() !== 'printing') return;
    const context = activeCheckout;
    options.observer.addBreadcrumb('entr.print', 'Print hang timeout fired');
    options.observer.captureMessage(
      'ENTR print hang exceeded timeout',
      'error',
      {
        timeout_ms: printHangEndsAtMs - printHangStartedAtMs,
      }
    );
    abortActivePrint();
    options.printState.value = 'failed';

    if (!context || !isActiveCheckout(context)) {
      activeCheckout = null;
      options.setSheet({
        kind: 'error',
        titleKey: 'errors.printTimeoutTitle',
        bodyKey: 'errors.printTimeoutBody',
      });
      options.scheduleResetToAttract(
        'print-timeout',
        PRINT_HANG_FAILURE_RESET_MS
      );
      return;
    }

    if (printAttempts >= MAX_PRINT_ATTEMPTS) {
      showFinalFailure('print');
      return;
    }

    // The retry sends the same print key, and the payment stays settled. The
    // backend does not print a key again while its job is queued, in progress
    // or completed. Thus, the retry cannot charge again, and it prints again
    // only a job that failed.
    showError(
      'errors.printTimeoutTitle',
      printFailureBodyKey('errors.printTimeoutRetryBody'),
      {
        retry: () => void startPrinting(context),
        helpCode: options.helpCode,
      }
    );
    schedulePrintFailureReset();
  }

  return {
    continueFromSelection,
    cancelActivePaymentForReset,
    resetForAttract,
    goBackFromPayment,
    tryVoucherScan,
    isWaitingForCard,
    onLeavingPrinting,
    stop,
  };
}
